import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { isEmpty } from "@medplum/core";
import { Condition, Task } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Button, Checkbox, Drawer, Group, Stack } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import {
  completeChecklistTasks,
  createCloseoutTask,
  getCloseoutTask,
} from "@/clients/services/checklist-task";
import ConfirmationModal from "@/components/ConfirmationModal";
import { TextWrapper } from "@/components/TextWrapper";
import { ChecklistTaskStatus } from "@/constants/checklist";
import { fetchActivePregnancyCondition } from "@/pregnancy";
import { useAppDispatch } from "@/store";
import {
  completeChecklistTasksAction,
  getActiveChecklist,
  getUncompletedTasksByChecklistId,
} from "@/store/slices/checklists";
import { ChecklistTask } from "@/types/checklist-task";
import { getCodeableConceptDisplay } from "@/utils/medplum";
import { showSystemErrorNotification, toast } from "@/utils/notifications";

type ChecklistCloseoutDrawerProps = {
  opened: boolean;
  onClose: () => void;
};

function ChecklistCloseoutDrawer({ opened, onClose }: ChecklistCloseoutDrawerProps) {
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const { clientId } = useParams() as { clientId: string };
  const getUncompletedTasks = useSelector(getUncompletedTasksByChecklistId);
  const [isDrawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [isSaving, setIsSaving] = useState(false);
  const [closeoutTask, setCloseoutTask] = useState<Task | undefined>(undefined);
  const [selectedChecklists, setSelectedChecklists] = useState<string[]>([]);
  const [pregnancyCondition, setPregnancyCondition] = useState<Condition | undefined>(undefined);

  useEffect(() => {
    (async () => {
      const condition = await fetchActivePregnancyCondition(medplum, clientId);
      setPregnancyCondition(condition);

      const task = await getCloseoutTask(medplum, clientId, condition?.id);
      setCloseoutTask(task);
    })();
  }, [clientId, medplum]);

  const onChange = (checklistId: string) => {
    setSelectedChecklists((prev) =>
      prev.includes(checklistId) ? prev.filter((id) => id !== checklistId) : [...prev, checklistId],
    );
  };

  const onConfirm = async () => {
    if (!closeoutTask) {
      await createCloseoutTask({ medplum, clientId });
    }
    openDrawer();
    onClose();
  };

  const onCloseModal = async () => {
    if (!closeoutTask) {
      const task = await createCloseoutTask({ medplum, clientId });
      setCloseoutTask(task);
    }
    onClose();
  };

  const onCloseDrawer = () => {
    closeDrawer();
    onClose();
  };

  const onSave = async () => {
    setIsSaving(true);
    try {
      const uncompletedTasks = selectedChecklists
        .map((checklistId) => getUncompletedTasks(checklistId))
        .flat();

      if (isEmpty(uncompletedTasks)) return;

      await completeChecklistTasks(medplum, uncompletedTasks);

      dispatch(completeChecklistTasksAction({ checklistIds: selectedChecklists }));

      toast({
        title: "Checklists closed",
        message: "Checklists closed successfully.",
        type: "success",
        autoClose: 3000,
      });
    } catch (error) {
      showSystemErrorNotification();
      throw error;
    } finally {
      setIsSaving(false);
      onCloseDrawer();
    }
  };

  if (!pregnancyCondition && closeoutTask) return null;

  return (
    <React.Fragment>
      <ConfirmationModal
        opened={opened}
        isDeleting={false}
        onConfirm={onConfirm}
        onClose={onCloseModal}
        cancelButtonText="No, Keep All Open"
        confirmButtonText="Review Active Checklist"
        description="You can review active checklists to close them out now."
        title="Just a reminder, you still have active checklists for this pregnancy. "
      />
      <Drawer.Root
        size={512}
        position="right"
        closeOnEscape={false}
        opened={isDrawerOpened}
        onClose={onCloseDrawer}
        closeOnClickOutside={false}
      >
        <Drawer.Overlay />

        <Drawer.Content>
          <Drawer.Header bg="gray.0">
            <Group align="center" justify="space-between">
              <Drawer.CloseButton />
              <Drawer.Title>Select Checklists to Close</Drawer.Title>
            </Group>
            <Button
              type="submit"
              onClick={onSave}
              loading={isSaving}
              disabled={selectedChecklists.length === 0}
            >
              Save
            </Button>
          </Drawer.Header>
          <Drawer.Body my="md">
            <ChecklistCloseoutDrawerBody
              onChange={onChange}
              selectedChecklists={selectedChecklists}
            />
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Root>
    </React.Fragment>
  );
}

function ChecklistCloseoutDrawerBody({
  selectedChecklists,
  onChange,
}: {
  selectedChecklists: string[];
  onChange: (checklistId: string) => void;
}) {
  const activeChecklists = useSelector(getActiveChecklist);

  return (
    <Stack>
      {activeChecklists.map((checklist) => (
        <ChecklistCloseoutDrawerTask
          key={checklist.id}
          onChange={onChange}
          checklist={checklist}
          selectedChecklists={selectedChecklists}
        />
      ))}
    </Stack>
  );
}

const ChecklistCloseoutDrawerTask = ({
  checklist,
  onChange,
  selectedChecklists,
}: {
  checklist: ChecklistTask;
  onChange: (checklistId: string) => void;
  selectedChecklists: string[];
}) => {
  const allTasks = Object.values(checklist.tasks);
  const completedTasks = allTasks.filter((task) => task.status === ChecklistTaskStatus.Completed);

  return (
    <Group align="center" key={checklist.id} justify="space-between">
      <Stack gap="xs">
        <TextWrapper fw="bold">{getCodeableConceptDisplay(checklist.code)}</TextWrapper>
        <TextWrapper fw="bold" c="gray.6">
          {completedTasks.length} of {allTasks.length} tasks completed
        </TextWrapper>
      </Stack>
      <Checkbox
        value={checklist.id}
        onChange={() => onChange(checklist.id!)}
        checked={selectedChecklists.includes(checklist.id!)}
      />
    </Group>
  );
};

export default ChecklistCloseoutDrawer;
